import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { PageHeader } from './ui';
import './ValifiCreditCheck.css';

interface CreditCheckJob {
  id: string;
  status: 'initializing' | 'processing' | 'uploading' | 'completed' | 'failed';
  startedAt: string;
  startedBy: string;
  recipientEmail?: string;
  totalClaims: number;
  processedClaims: number;
  equifaxCompleted: number;
  transunionCompleted: number;
  agreementsFound: number;
  provider?: 'both' | 'equifax' | 'transunion';
  errorCount: number;
  progressPercentage: number;
  completedAt?: string;
  excelReportReady: boolean;
  downloadUrl?: string;
  emailSent?: boolean;
  error?: string;
}

interface HealthStatus {
  status: string;
  authenticated: boolean;
  base_url?: string;
  token_expires?: string;
  error?: string;
}

interface BulkImportJob {
  id: string;
  status: 'initializing' | 'parsing' | 'processing' | 'uploading' | 'completed' | 'failed';
  startedAt: string;
  startedBy: string;
  recipientEmail?: string;
  totalRows: number;
  processedRows: number;
  createdClaims: number;
  skippedExisting: number;
  skippedNoClient: number;
  skippedNonViableLender: number;
  errorCount: number;
  progressPercentage: number;
  completedAt?: string;
  downloadUrl?: string;
  emailSent?: boolean;
  error?: string;
}

type ImportType = 'claim_ids' | 'client_data';

const ValifiCreditCheck: React.FC = () => {
  const { apiRequest } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importType, setImportType] = useState<ImportType>('claim_ids');
  const [provider, setProvider] = useState<'both' | 'equifax' | 'transunion'>('both');
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeJobs, setActiveJobs] = useState<CreditCheckJob[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [autoRefreshJobs, setAutoRefreshJobs] = useState(false);
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const jobsPollInterval = useRef<NodeJS.Timeout | null>(null);

  // Bulk import state
  const [bulkImportFile, setBulkImportFile] = useState<File | null>(null);
  const [isBulkImporting, setIsBulkImporting] = useState(false);
  const [bulkImportJob, setBulkImportJob] = useState<BulkImportJob | null>(null);
  const bulkImportFileInputRef = useRef<HTMLInputElement>(null);
  const bulkImportPollInterval = useRef<NodeJS.Timeout | null>(null);

  // Fetch active jobs
  const fetchActiveJobs = async () => {
    try {
      const response = await apiRequest<{ jobs: CreditCheckJob[] }>({
        url: '/ui/admin/valifi-credit-check/active-jobs',
        method: 'GET'
      });
      setActiveJobs(response.jobs || []);
    } catch (error) {
      console.error('Error fetching active jobs:', error);
    }
  };

  // Initial load
  useEffect(() => {
    fetchActiveJobs();
  }, []);

  // Auto-refresh active jobs
  useEffect(() => {
    if (autoRefreshJobs) {
      jobsPollInterval.current = setInterval(fetchActiveJobs, 5000);
      return () => {
        if (jobsPollInterval.current) {
          clearInterval(jobsPollInterval.current);
        }
      };
    }
  }, [autoRefreshJobs]);

  // Check API health
  const checkHealth = async () => {
    setIsCheckingHealth(true);
    try {
      const response = await apiRequest<{ health: HealthStatus }>({
        url: '/ui/admin/valifi-credit-check/health',
        method: 'GET'
      });
      setHealthStatus(response.health);
    } catch (error: any) {
      setHealthStatus({
        status: 'unhealthy',
        authenticated: false,
        error: error.message || 'Failed to check health'
      });
    } finally {
      setIsCheckingHealth(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls') && !fileName.endsWith('.csv')) {
        setError('Please select an Excel (.xlsx, .xls) or CSV file');
        return;
      }
      // Check file size (limit to 50MB)
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        setError('File size exceeds 50MB limit.');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  // Start credit check
  const handleStartCreditCheck = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('provider', provider);

      // Use different endpoint based on import type
      const endpoint = importType === 'client_data'
        ? '/ui/admin/valifi-credit-check/start-client-data'
        : '/ui/admin/valifi-credit-check/start';

      const response = await apiRequest<{
        jobId: string;
        totalClaims?: number;
        totalClients?: number;
        recipientEmail?: string;
        message?: string;
      }>({
        url: endpoint,
        method: 'POST',
        data: formData
      });

      // Show success message - no need to poll, user will get email
      const totalCount = response.totalClaims || response.totalClients || 0;
      setSuccessMessage(
        `Credit check started for ${totalCount} records. You will receive an email at ${response.recipientEmail} when the report is ready. You can navigate away from this page.`
      );

      setSelectedFile(null);
      setIsProcessing(false);

      // Refresh active jobs list
      fetchActiveJobs();

    } catch (error: any) {
      setIsProcessing(false);
      setError(error.message || 'Failed to start credit check');
    }
  };

  // Download template
  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const response = await fetch(`${apiUrl}/ui/admin/valifi-credit-check/template`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'valifi_credit_check_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading template:', error);
      setError('Failed to download template');
    }
  };

  // Download client data template
  const handleDownloadClientDataTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const response = await fetch(`${apiUrl}/ui/admin/valifi-credit-check/template-client-data`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'valifi_credit_check_client_data_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading client data template:', error);
      setError('Failed to download template');
    }
  };

  // Clean up intervals on unmount
  useEffect(() => {
    return () => {
      if (jobsPollInterval.current) {
        clearInterval(jobsPollInterval.current);
      }
      if (bulkImportPollInterval.current) {
        clearInterval(bulkImportPollInterval.current);
      }
    };
  }, []);

  // Bulk Import: Handle file selection
  const handleBulkImportFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
        setError('Please select an Excel file (.xlsx, .xls) for bulk import');
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        setError('File size exceeds 50MB limit.');
        return;
      }
      setBulkImportFile(file);
      setError(null);
    }
  };

  // Bulk Import: Start import
  const handleStartBulkImport = async () => {
    if (!bulkImportFile) {
      setError('Please select a file');
      return;
    }

    setIsBulkImporting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const formData = new FormData();
      formData.append('file', bulkImportFile);

      const response = await apiRequest<{
        jobId: string;
        recipientEmail?: string;
        message?: string;
      }>({
        url: '/ui/admin/valifi-credit-check/bulk-import/start',
        method: 'POST',
        data: formData
      });

      setSuccessMessage(
        `Bulk import started. You will receive an email at ${response.recipientEmail} when the import is complete.`
      );
      setBulkImportFile(null);
      setIsBulkImporting(false);

      // Start polling for job status
      if (response.jobId) {
        pollBulkImportStatus(response.jobId);
      }

    } catch (err: any) {
      setIsBulkImporting(false);
      setError(err.message || 'Failed to start bulk import');
    }
  };

  // Bulk Import: Poll job status
  const pollBulkImportStatus = (jobId: string) => {
    // Clear any existing poll interval
    if (bulkImportPollInterval.current) {
      clearInterval(bulkImportPollInterval.current);
    }

    const poll = async () => {
      try {
        const response = await apiRequest<{ job: BulkImportJob }>({
          url: `/ui/admin/valifi-credit-check/bulk-import/${jobId}/status`,
          method: 'GET'
        });

        setBulkImportJob(response.job);

        if (response.job.status === 'completed' || response.job.status === 'failed') {
          if (bulkImportPollInterval.current) {
            clearInterval(bulkImportPollInterval.current);
            bulkImportPollInterval.current = null;
          }
        }
      } catch (err) {
        console.error('Error polling bulk import status:', err);
        if (bulkImportPollInterval.current) {
          clearInterval(bulkImportPollInterval.current);
          bulkImportPollInterval.current = null;
        }
      }
    };

    // Initial poll
    poll();
    // Then poll every 3 seconds
    bulkImportPollInterval.current = setInterval(poll, 3000);
  };

  // Bulk Import: Download template
  const handleDownloadBulkImportTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const response = await fetch(`${apiUrl}/ui/admin/valifi-credit-check/bulk-import/template`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'bulk_claim_import_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error('Error downloading bulk import template:', err);
      setError('Failed to download template');
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'processing':
        return 'bg-blue-500 text-white animate-pulse';
      case 'initializing':
        return 'bg-amber-500 text-white';
      case 'uploading':
        return 'bg-purple-500 text-white animate-pulse';
      case 'completed':
        return 'bg-emerald-500 text-white';
      case 'failed':
        return 'bg-red-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  return (
    <div className="mb-8 rounded-xl border-2 border-teal-500 bg-gradient-to-br from-teal-50 to-white p-6 shadow-md transition-all duration-300 hover:shadow-lg hover:border-teal-600">
      <PageHeader
        title="Valifi Credit Check"
        description="Run batch credit checks on claims to find motor finance agreements. Upload an Excel file with claim IDs to get started."
      />

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-700 animate-slide-in">
          <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500 text-sm font-bold text-white">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </span>
          <div>
            <span className="font-medium">{successMessage}</span>
            <button
              className="mt-2 block text-sm text-emerald-600 underline hover:text-emerald-800"
              onClick={() => setSuccessMessage(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 animate-slide-in">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-sm font-bold text-white">!</span>
          <span className="font-medium">{error}</span>
        </div>
      )}

      {/* API Health Status */}
      <div className="mb-6 flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 bg-white p-4">
        <button
          className="rounded-lg border-2 border-blue-500 bg-white px-4 py-2 text-sm font-semibold text-blue-500 transition-all duration-200 hover:bg-blue-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
          onClick={checkHealth}
          disabled={isCheckingHealth}
        >
          {isCheckingHealth ? 'Checking...' : 'Check API Connection'}
        </button>
        {healthStatus && (
          <div className={`flex items-center gap-2 text-sm ${healthStatus.status === 'healthy' ? 'text-emerald-600' : 'text-red-600'}`}>
            <span
              className={`h-3 w-3 rounded-full ${healthStatus.status === 'healthy' ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]' : 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]'}`}
            />
            <span>
              {healthStatus.status === 'healthy'
                ? `Connected to Valifi (${healthStatus.base_url})`
                : `Not connected: ${healthStatus.error || 'Unknown error'}`
              }
            </span>
          </div>
        )}
      </div>

      {/* Upload Controls */}
      <div className="grid gap-6">
        {/* File Upload Section */}
        <div className="rounded-xl border-2 border-gray-200 bg-white p-6 shadow-sm transition-all duration-200 hover:border-teal-300 hover:shadow-md">
          {/* Import Type Selection */}
          <div className="mb-5">
            <h4 className="mb-3 text-lg font-semibold text-teal-700">Import Type</h4>
            <div className="flex flex-wrap gap-4">
              <label className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 p-4 transition-all duration-200 ${
                importType === 'claim_ids'
                  ? 'border-teal-500 bg-teal-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-teal-300'
              }`}>
                <input
                  type="radio"
                  name="importType"
                  value="claim_ids"
                  checked={importType === 'claim_ids'}
                  onChange={() => { setImportType('claim_ids'); setSelectedFile(null); }}
                  className="h-4 w-4 text-teal-600"
                />
                <div>
                  <div className="font-semibold text-gray-800">Existing Claims</div>
                  <div className="text-xs text-gray-500">Upload claim IDs to check existing clients</div>
                </div>
              </label>

              <label className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 p-4 transition-all duration-200 ${
                importType === 'client_data'
                  ? 'border-teal-500 bg-teal-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-teal-300'
              }`}>
                <input
                  type="radio"
                  name="importType"
                  value="client_data"
                  checked={importType === 'client_data'}
                  onChange={() => { setImportType('client_data'); setSelectedFile(null); }}
                  className="h-4 w-4 text-teal-600"
                />
                <div>
                  <div className="font-semibold text-gray-800">New Client Data</div>
                  <div className="text-xs text-gray-500">Upload client details directly (no existing claims needed)</div>
                </div>
              </label>
            </div>
          </div>

          {/* Credit Bureau Provider Selection */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '14px' }}>
              Credit Bureau Provider
            </label>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value as 'both' | 'equifax' | 'transunion')}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                backgroundColor: 'white',
                width: '100%',
                maxWidth: '300px',
              }}
            >
              <option value="both">Both (Equifax + TransUnion)</option>
              <option value="equifax">Equifax Only</option>
              <option value="transunion">TransUnion Only</option>
            </select>
          </div>

          <h4 className="mb-2 text-lg font-semibold text-teal-700">
            {importType === 'claim_ids' ? 'Upload Claim IDs' : 'Upload Client Data'}
          </h4>
          <p className="mb-4 text-sm text-gray-500">
            {importType === 'claim_ids'
              ? 'Excel file with a single column of claim IDs (e.g., FC100001.001)'
              : 'Excel file with client details: name, DOB, address, postcode. Download the template for the correct format.'
            }
          </p>

          <div className="mb-4 flex flex-wrap items-center gap-4 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-4 transition-all duration-200 hover:border-teal-400 hover:bg-teal-50">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              disabled={isProcessing}
              className="hidden"
            />
            <button
              className="rounded-lg bg-teal-500 px-5 py-2.5 font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-teal-600 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60 disabled:transform-none"
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing}
            >
              Choose File
            </button>
            <span className="flex-1 truncate text-sm text-gray-500">
              {selectedFile ? selectedFile.name : 'No file selected'}
            </span>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              className="rounded-lg bg-emerald-500 px-6 py-3 font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-emerald-600 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60 disabled:transform-none"
              onClick={handleStartCreditCheck}
              disabled={!selectedFile || isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Start Credit Check'}
            </button>
            <button
              className="rounded-lg bg-blue-500 px-6 py-3 font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-blue-600 hover:shadow-md"
              onClick={importType === 'claim_ids' ? handleDownloadTemplate : handleDownloadClientDataTemplate}
            >
              Download Template
            </button>
          </div>
        </div>

        {/* Active Jobs Section */}
        <div className="rounded-xl border-2 border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
            <h4 className="text-lg font-semibold text-teal-700">Active Jobs</h4>
            <div className="flex items-center gap-4">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-500">
                <input
                  type="checkbox"
                  checked={autoRefreshJobs}
                  onChange={(e) => setAutoRefreshJobs(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-teal-500 focus:ring-teal-500"
                />
                Auto-refresh
              </label>
              <button
                className="rounded-lg border-2 border-teal-500 bg-white px-4 py-1.5 text-sm font-semibold text-teal-600 transition-all duration-200 hover:bg-teal-500 hover:text-white"
                onClick={fetchActiveJobs}
              >
                Refresh
              </button>
            </div>
          </div>

          {activeJobs.length === 0 ? (
            <p className="py-8 text-center text-gray-400 italic">No active credit check jobs</p>
          ) : (
            <div className="grid gap-4">
              {activeJobs.map((job) => (
                <div
                  key={job.id}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-4 transition-all duration-200 hover:border-teal-300 hover:shadow-sm"
                >
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <span className="font-semibold text-gray-700">Job by {job.startedBy}</span>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${getStatusBadgeClass(job.status)}`}>
                      {job.status}
                    </span>
                    {job.provider && job.provider !== 'both' && (
                      <span style={{
                        fontSize: '11px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: job.provider === 'equifax' ? '#dbeafe' : '#fce7f3',
                        color: job.provider === 'equifax' ? '#1e40af' : '#9d174d',
                        marginLeft: '8px',
                      }}>
                        {job.provider === 'equifax' ? 'Equifax Only' : 'TransUnion Only'}
                      </span>
                    )}
                  </div>
                  <div className="mb-3 grid grid-cols-2 gap-2 text-sm text-gray-600 sm:grid-cols-4">
                    <span>Progress: {job.progressPercentage}%</span>
                    <span>Claims: {job.processedClaims}/{job.totalClaims}</span>
                    <span>Agreements: {job.agreementsFound}</span>
                    <span>Errors: {job.errorCount}</span>
                  </div>
                  {job.status === 'completed' && job.emailSent && (
                    <div className="flex items-center gap-2 text-sm text-emerald-600">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span>Report emailed to {job.recipientEmail}</span>
                    </div>
                  )}
                  {job.status === 'completed' && job.downloadUrl && (
                    <a
                      href={job.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-emerald-600 hover:shadow-md"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download Report
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bulk Import Claims Section */}
        <div className="rounded-xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 shadow-sm">
          <h4 className="mb-2 text-lg font-semibold text-amber-700">
            Bulk Import Claims from Credit Check Results
          </h4>
          <p className="mb-4 text-sm text-gray-500">
            Create new CL-owned claims from credit check export data. Upload the XLSX export from credit checks -
            rows without an existing claim will have new claims created using the client's CL sequence.
          </p>

          <div className="mb-4 flex flex-wrap items-center gap-4 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/50 p-4 transition-all duration-200 hover:border-amber-400">
            <input
              ref={bulkImportFileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleBulkImportFileSelect}
              disabled={isBulkImporting}
              className="hidden"
            />
            <button
              className="rounded-lg bg-amber-500 px-5 py-2.5 font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-amber-600 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60 disabled:transform-none"
              onClick={() => bulkImportFileInputRef.current?.click()}
              disabled={isBulkImporting}
            >
              Choose Credit Check Export
            </button>
            <span className="flex-1 truncate text-sm text-gray-500">
              {bulkImportFile ? bulkImportFile.name : 'No file selected'}
            </span>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              className="rounded-lg bg-amber-600 px-6 py-3 font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-amber-700 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60 disabled:transform-none"
              onClick={handleStartBulkImport}
              disabled={!bulkImportFile || isBulkImporting}
            >
              {isBulkImporting ? 'Importing...' : 'Create Claims from Export'}
            </button>
            <button
              className="rounded-lg bg-gray-500 px-6 py-3 font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-gray-600 hover:shadow-md"
              onClick={handleDownloadBulkImportTemplate}
            >
              Download Template
            </button>
          </div>

          {/* Bulk Import Progress */}
          {bulkImportJob && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-white p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-gray-700">Import Progress</span>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${getStatusBadgeClass(bulkImportJob.status)}`}>
                  {bulkImportJob.status}
                </span>
              </div>

              {/* Progress bar */}
              <div className="mb-3 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full bg-amber-500 transition-all duration-300"
                  style={{ width: `${bulkImportJob.progressPercentage}%` }}
                />
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 sm:grid-cols-6">
                <span>Progress: {bulkImportJob.progressPercentage}%</span>
                <span>Rows: {bulkImportJob.processedRows}/{bulkImportJob.totalRows}</span>
                <span className="text-emerald-600">Created: {bulkImportJob.createdClaims}</span>
                <span className="text-blue-600">Skipped: {bulkImportJob.skippedExisting + bulkImportJob.skippedNoClient + bulkImportJob.skippedNonViableLender}</span>
                <span className="text-amber-700">Non-viable lenders: {bulkImportJob.skippedNonViableLender}</span>
                <span className="text-red-600">Errors: {bulkImportJob.errorCount}</span>
              </div>

              {bulkImportJob.status === 'completed' && bulkImportJob.downloadUrl && (
                <a
                  href={bulkImportJob.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-emerald-600 hover:shadow-md"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Import Report
                </a>
              )}

              {bulkImportJob.status === 'failed' && bulkImportJob.error && (
                <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <strong>Error:</strong> {bulkImportJob.error}
                </div>
              )}
            </div>
          )}

          {/* Info box about how it works */}
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
            <strong className="text-gray-700">How it works:</strong>
            <ul className="mt-2 list-inside list-disc space-y-1">
              <li>Upload the XLSX export from the credit check results</li>
              <li>Rows where "Existing Claim" column has a value will be skipped</li>
              <li>The "Claim ID" column is used to find the existing client</li>
              <li>New claims are created with CL references continuing the client's sequence</li>
              <li>Lender, agreement number, dates, and other data are imported to the new claim</li>
            </ul>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ValifiCreditCheck;
